import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography, alpha, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import {
    TrophyOutlined,
    AlertOutlined,
    FileTextOutlined,
    AudioOutlined,
    CommentOutlined,
    LineChartOutlined,
    ArrowRightOutlined,
    CheckCircleFilled
} from '@ant-design/icons';

import { ArtAddon, ArtDeadline, ArtVoice, ArtPlan, ArtAssess, ArtGrowth } from './FeatureCardArt';
import AnimateButton from 'components/@extended/AnimateButton';
import { PageUrl } from 'const/page-url';
import { SECTION_PY } from 'const/section';

// ==============================|| FEATURE OVERVIEW SECTION ||============================== //
//
// 機能の「概要」をカード一覧で提示する。LP 上の機能紹介はこのセクションに集約しており、
// STEP 形式の詳細セクションは置いていない。
//
// 並び順は意図的に「売上を守る・増やす」を先頭に置いている。決裁者(経営者)が
// 最初に目にする情報を、業務効率ではなく収益インパクトにするため。
//
// image を持たないカードは、実際の画面キャプチャがまだ用意できていないもの。
// 実物と異なるモック画像を置くと誤解を招くため、抽象的なパネルで代替している。
// キャプチャが用意でき次第 image に差し替えること。

/**
 * カード下部の項目。強調したい語がある場合は
 * { text, highlight } の形で、text 中の highlight 部分だけを赤字にする。
 */
type FeatureItem = string | { text: string; highlight: string };

type Feature = {
    title: string;
    /** カードの中でいちばん目立つ一文。ベネフィットを言い切る */
    benefit: string;
    icon: JSX.Element;
    group: string;
    items: FeatureItem[];
    /** 画面イメージ枠に置く線画（FeatureCardArt） */
    art: JSX.Element;
};

const primaryColor = '#2aa1af';
const accentRed = '#e2574c';

const features: Feature[] = [
    {
        group: '売上を守る・増やす',
        title: '加算取得をサポート',
        benefit: '算定に必要な書類をAIが下書き',
        icon: <TrophyOutlined />,
        art: <ArtAddon />,
        items: [
            { text: '専門的支援実施計画書', highlight: '専門的支援実施計画書' },
            '不登校支援の報告書',
            '関係機関との連携書類'
        ]
    },
    {
        group: '売上を守る・増やす',
        title: '減算対策とアラート',
        benefit: '期限切れを事前にお知らせ！\nうっかり減算をゼロに',
        icon: <AlertOutlined />,
        art: <ArtDeadline />,
        items: [
            '支援プログラムの作成',
            '自己評価の作成',
            { text: '個別支援計画書・モニタリングの期限アラート', highlight: '個別支援計画書' }
        ]
    },
    {
        group: '書類業務を減らす',
        title: '個別支援計画書の自動作成',
        benefit: 'アセスメントからAIが目標提案、\n計画書へ自動反映',
        icon: <FileTextOutlined />,
        art: <ArtPlan />,
        items: [{ text: '5領域の支援目標を自動設定', highlight: '5領域の支援目標を自動設定' }, 'アセスメントと連動', 'モニタリングまで一貫']
    },
    {
        group: '書類業務を減らす',
        title: '音声入力とAI下書き',
        benefit: '話すだけで記録が完成！\n記録した内容はAIが整形',
        icon: <AudioOutlined />,
        art: <ArtVoice />,
        items: [{ text: '日報', highlight: '日報' }, '面談の議事録', '職員間の申し送り']
    },
    {
        group: '支援の質を上げる',
        title: '5領域のアセスメント',
        benefit: 'AIとの対話形式で根拠のあるアセスメントを実施',
        icon: <CommentOutlined />,
        art: <ArtAssess />,
        items: ['対話式のアセスメント', 'その子ならではの特性を把握']
    },
    {
        group: '支援の質を上げる',
        title: 'モニタリングと成長データ',
        benefit: '成長の履歴がそのまま残る！\n次回の個別支援計画作成にも活用',
        icon: <LineChartOutlined />,
        art: <ArtGrowth />,
        items: ['成長履歴の蓄積', '過去の計画を参照', '変遷の比較']
    }
];

/** highlight に指定された語だけを赤字にして描画する */
const renderItemText = (item: FeatureItem) => {
    if (typeof item === 'string') return item;

    const index = item.text.indexOf(item.highlight);
    if (index === -1) return item.text;

    return (
        <>
            {item.text.slice(0, index)}
            <Box component="span" sx={{ color: accentRed }}>
                {item.highlight}
            </Box>
            {item.text.slice(index + item.highlight.length)}
        </>
    );
};

const FeatureOverviewSection = () => {
    /**
     * 線画を置く枠。
     *
     * 高さは比率ではなく固定値にしている。比率指定にするとカード幅に連動して
     * 枠が高くなり、6枚分でスクロール量が大きく増えるため。
     */
    const renderArtPanel = (art: JSX.Element) => (
        <Box
            aria-hidden="true"
            sx={{
                width: '100%',
                height: '100%',
                borderRadius: '10px',
                border: `1px solid ${alpha(primaryColor, 0.15)}`,
                background: `linear-gradient(150deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(primaryColor, 0.03)} 60%, #fff 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}
        >
            {art}
        </Box>
    );

    const renderFeatureCard = (feature: Feature, index: number) => (
        <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.07 }}
                style={{ height: '100%' }}
            >
                <Box
                    component="article"
                    sx={{
                        height: '100%',
                        // 6枚を正方形で揃える。中身の量はカードごとに違うので、
                        // 余った縦を画面イメージ枠(flex:1)が吸収する
                        aspectRatio: '1 / 1',
                        // 正方形なので幅がそのまま高さになる。列幅いっぱいに広げると
                        // 1枚が大きすぎて画面イメージ枠だけが間延びするため、上限を決めて中央に置く
                        width: '100%',
                        maxWidth: { xs: 320, sm: 340, md: 330 },
                        mx: 'auto',
                        p: { xs: 1.75, md: 2 },
                        borderRadius: '16px',
                        bgcolor: '#fff',
                        border: `1px solid ${alpha(primaryColor, 0.14)}`,
                        boxShadow: `0 10px 30px ${alpha(primaryColor, 0.05)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 16px 34px ${alpha(primaryColor, 0.13)}`,
                            borderColor: primaryColor
                        }
                    }}
                >
                    {/* アイコン＋タイトル（タイトルが2行になるカードがあるため、高さを揃えて区切り線の位置を合わせる） */}
                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.1, minHeight: 40 }}>
                        <Box
                            aria-hidden="true"
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '9px',
                                bgcolor: alpha(primaryColor, 0.09),
                                color: primaryColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.95rem',
                                flexShrink: 0
                            }}
                        >
                            {feature.icon}
                        </Box>
                        <Typography
                            variant="h3"
                            component="h3"
                            sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '0.95rem' }, color: '#1a1a1a', lineHeight: 1.35 }}
                        >
                            {feature.title}
                        </Typography>
                    </Stack>

                    <Box sx={{ height: '1px', bgcolor: alpha(primaryColor, 0.12), mb: 1.1 }} aria-hidden="true" />

                    {/* ベネフィット */}
                    <Typography
                        component="p"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '0.85rem', md: '0.88rem' },
                            color: '#374151',
                            lineHeight: 1.5,
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                            mb: 1.25
                        }}
                    >
                        {feature.benefit}
                    </Typography>

                    {/* 画面イメージ */}
                    <Box sx={{ mb: 1.25, flex: 1, minHeight: 0 }}>{renderArtPanel(feature.art)}</Box>

                    {/* 含まれるもの */}
                    <Stack spacing={0.6} component="ul" sx={{ p: 0, listStyle: 'none', m: 0, mt: 'auto' }}>
                        {feature.items.map((item) => {
                            const text = typeof item === 'string' ? item : item.text;
                            return (
                                <Stack key={text} direction="row" spacing={1} component="li" alignItems="flex-start">
                                    <CheckCircleFilled
                                        aria-hidden="true"
                                        style={{ color: alpha(primaryColor, 0.75), fontSize: '0.72rem', marginTop: '3px', flexShrink: 0 }}
                                    />
                                    <Typography component="span" sx={{ color: primaryColor, fontSize: '0.74rem', lineHeight: 1.45, fontWeight: 700 }}>
                                        {renderItemText(item)}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>

                    <Box sx={{ textAlign: 'right', mt: 1, color: alpha(primaryColor, 0.45), fontSize: '0.85rem', lineHeight: 1 }} aria-hidden="true">
                        <ArrowRightOutlined />
                    </Box>
                </Box>
            </motion.div>
        </Grid>
    );

    return (
        <Box component="section" sx={{ py: SECTION_PY, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 3.5, md: 5 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.35 }}
                    >
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 1.25 }}
                        >
                            FEATURES
                        </Typography>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', md: '2.1rem' }, color: '#1a1a1a', lineHeight: 1.3 }}
                        >
                            <Box component="span" sx={{ color: primaryColor }}>OHANA AI</Box> の機能
                        </Typography>
                    </motion.div>
                </Box>

                <Grid container spacing={2} alignItems="stretch">
                    {features.map((feature, index) => renderFeatureCard(feature, index))}
                </Grid>

                {/*
                  * 機能詳細ページ(/features)への導線。
                  * STEP 01〜06 の詳細はトップに置くと縦が長くなりすぎるため別ページに切り出してあり、
                  * 続きを読みたい人だけがここから移動する。
                  */}
                <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 5 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <AnimateButton>
                            <Button
                                component={RouterLink}
                                to={PageUrl.Features.urlWithSlash}
                                size="large"
                                variant="outlined"
                                endIcon={<ArrowRightOutlined aria-hidden="true" />}
                                sx={{
                                    color: primaryColor,
                                    borderColor: alpha(primaryColor, 0.5),
                                    px: 4,
                                    py: 1.3,
                                    borderRadius: '14px',
                                    fontSize: '0.95rem',
                                    fontWeight: 800,
                                    bgcolor: '#fff',
                                    '&:hover': {
                                        borderColor: primaryColor,
                                        bgcolor: alpha(primaryColor, 0.06)
                                    }
                                }}
                            >
                                機能の詳細を見る
                            </Button>
                        </AnimateButton>
                    </motion.div>
                </Box>

                {/*
                  * アセスメントの性格についての注記。
                  * 「5領域のアセスメント」を機能として掲げているため、脳科学的アプローチによる
                  * 課題特定・原因診断ではないことをここで断っておく（特許回避方針）。
                  * 文言は FeatureSection STEP01 の注記と揃えてある。
                  */}
                <Typography
                    component="p"
                    sx={{
                        mt: { xs: 3, md: 4 },
                        textAlign: 'center',
                        color: alpha('#000', 0.38),
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        lineHeight: 1.7,
                        fontWeight: 500
                    }}
                >
                    ※本アセスメントは脳科学的アプローチによる課題特定を目的としたものではありません。
                </Typography>
            </Container>
        </Box>
    );
};

export default FeatureOverviewSection;
